// What a ledger entry is called when a homeowner reads it.
//
// **Deliberately not a copy of `front-porch/src/billing/enumLabels.ts`.**
// That file is written for someone who does this for a living, and its
// vocabulary is the accounting system's: "assessment", "charge
// adjustment", "NSF fee". A homeowner opening their account should not
// have to translate. Where the two differ, the difference is the point:
//
//   assessment          Dues                 (same)
//   nsf_fee             NSF fee           →  Returned payment fee
//   charge_adjustment   Charge adjustment →  Correction
//   reversal            Reversal          →  Correction
//   credit_adjustment   Credit            →  Credit applied
//   payment             Payment           →  Payment received
//   refund              Refund            →  Refund issued to you
//   transfer_in         Transferred in    →  Balance carried forward
//
// Amounts, dates and the running balance are the same numbers on both
// screens. Only the words change.

/** Catalyst `Badge` colours, kept to the set that file exports. */
export type EntryColor = 'zinc' | 'amber' | 'lime' | 'sky' | 'purple';

interface EntryMeta {
  label: string;
  color: EntryColor;
  /** Shown in the row-detail dialog, where there is room to explain. */
  help?: string;
}

const ENTRIES: Record<string, EntryMeta> = {
  assessment: {
    label: 'Dues',
    color: 'zinc',
    help: 'Your regular association dues for the period.',
  },
  special_assessment: {
    label: 'Special assessment',
    color: 'zinc',
    help: 'A one-off levy the association charged on top of regular dues.',
  },
  late_fee: {
    label: 'Late fee',
    color: 'amber',
    help: 'Charged because a payment arrived after the grace date.',
  },
  interest: {
    label: 'Interest on overdue balance',
    color: 'amber',
    help: 'Interest the association charges on an amount past due.',
  },
  violation_fine: {
    label: 'Violation fine',
    color: 'amber',
    help: 'A fine for a cited violation. Open the violation for details.',
  },
  nsf_fee: {
    label: 'Returned payment fee',
    color: 'amber',
    help: 'Charged when a payment was returned unpaid by the bank.',
  },
  other_charge: {
    label: 'Charge',
    color: 'zinc',
  },
  charge_adjustment: {
    label: 'Correction',
    color: 'zinc',
    help: 'An earlier charge was too low, and this makes up the difference.',
  },
  refund: {
    label: 'Refund issued to you',
    color: 'zinc',
    help: 'Money returned to you, which raises your balance again.',
  },
  payment: {
    label: 'Payment received',
    color: 'lime',
  },
  waiver: {
    label: 'Waived by the association',
    color: 'sky',
    help: 'The association chose not to collect a charge it could have.',
  },
  write_off: {
    label: 'Written off',
    color: 'sky',
    help: 'The association removed this amount from your balance.',
  },
  credit_adjustment: {
    label: 'Credit applied',
    color: 'sky',
    help: 'An earlier charge was too high, and this credits the difference.',
  },
  reversal: {
    label: 'Correction',
    color: 'purple',
    help: 'This cancels an earlier entry. The two net to nothing.',
  },
  transfer_in: {
    label: 'Balance carried forward',
    color: 'zinc',
    help: "Carried over from the previous owner's account when the home changed hands.",
  },
  transfer_out: {
    label: 'Balance transferred',
    color: 'zinc',
    help: 'Moved off this account when the home changed hands.',
  },
  amenity_fee: {
    label: 'Amenity booking fee',
    color: 'zinc',
    help: 'The fee for a reservation the association approved.',
  },
  amenity_deposit: {
    label: 'Amenity deposit',
    color: 'zinc',
    help: 'Held against damage and given back after your booking unless something needs repair.',
  },
  amenity_fee_return: {
    label: 'Booking fee returned',
    color: 'sky',
    help: 'A booking fee you had paid, given back to your account.',
  },
  amenity_deposit_return: {
    label: 'Deposit returned',
    color: 'sky',
    help: 'A deposit you had paid, given back to your account.',
  },
};

/**
 * Title-case an unrecognised entry type rather than dropping it.
 *
 * A type the backend adds later shows up as a readable, unstyled row
 * instead of a blank one — visibly new rather than invisibly missing.
 */
const titleCase = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const entryLabel = (entryType?: string | null): string => {
  if (!entryType) return '—';
  return ENTRIES[entryType]?.label ?? titleCase(entryType);
};

export const entryColor = (entryType?: string | null): EntryColor =>
  (entryType && ENTRIES[entryType]?.color) || 'zinc';

export const entryHelp = (entryType?: string | null): string | undefined =>
  entryType ? ENTRIES[entryType]?.help : undefined;

/**
 * The activity feed's three filters, in place of front-porch's sixteen
 * entry-type chips.
 *
 * Sixteen chips is a report builder; nobody has ever wanted to see only
 * their `charge_adjustment` rows. Charges and payments are the two
 * questions a homeowner actually asks, and `all` is the default.
 *
 * `charges` is every type that raises the balance and `payments` every
 * type that lowers it, so the two partition the ledger — a row can
 * never fall outside both. Kept in step with `LedgerEntryType::is_charge`
 * / `is_credit` in `back-end-rs/src/api/v1/billing/models.rs`.
 */
export type ActivityFilter = 'all' | 'charges' | 'payments';

const CHARGE_TYPES = [
  'assessment',
  'special_assessment',
  'late_fee',
  'interest',
  'violation_fine',
  'nsf_fee',
  'other_charge',
  'charge_adjustment',
  'refund',
  'transfer_in',
  'amenity_fee',
  'amenity_deposit',
];

const CREDIT_TYPES = [
  'payment',
  'waiver',
  'write_off',
  'credit_adjustment',
  'transfer_out',
  'amenity_fee_return',
  'amenity_deposit_return',
];

/** The `entry_type` values behind a filter; `all` means no filter. */
export const filterTypes = (filter: ActivityFilter): ReadonlyArray<string> => {
  switch (filter) {
    case 'charges':
      return CHARGE_TYPES;
    case 'payments':
      return CREDIT_TYPES;
    default:
      return [];
  }
};

/** How a payment stands, in words a homeowner can act on. */
export const paymentStatusLabel = (status?: string | null): string => {
  switch (status) {
    case 'applied':
      return 'Applied';
    case 'overpaid':
      return 'Partly held';
    case 'returned':
      return 'Returned unpaid';
    case 'voided':
      return 'Voided';
    case 'refunded':
      return 'Refunded';
    default:
      return status ? titleCase(status) : '—';
  }
};

export const paymentMethodLabel = (method?: string | null): string => {
  switch (method) {
    case 'check':
      return 'Check';
    case 'ach':
      return 'Bank transfer';
    case 'echeck':
      return 'eCheck';
    case 'card':
      return 'Card';
    case 'cash':
      return 'Cash';
    case 'manual':
    case 'other':
      return 'Other';
    default:
      return method ? titleCase(method) : '—';
  }
};
